import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar as CalendarIcon, Tag, Newspaper, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";

const PRIMARY = "#4F1B7C";
const SECONDARY = "#EC1F7C";
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

// Category groups (slug -> label)
const CATEGORY_FILTERS = [
  { slug: "all", label: "Tous" },
  { slug: "pro", label: "Équipe Pro" },
  { slug: "feminine", label: "Féminines" },
  { slug: "amateur", label: "Centre de formation" },
  { slug: "partenaires", label: "Partenaires" },
  { slug: "club", label: "Vie du club" },
];

const EDITORIAL_TAGS = [
  { slug: "avant-match", label: "Avant-match" },
  { slug: "compte-rendu", label: "Compte-rendu" },
  { slug: "interview", label: "Interview" },
  { slug: "coulisses", label: "Coulisses" },
];

const PAGE_SIZE = 9; // 3 lines × 3 cards

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return ""; }
}

function ArticleCard({ a, size = "md", testid }) {
  const isXl = size === "xl";
  return (
    <Link
      to={`/sport/actualites/${a.slug}`}
      className="group relative block overflow-hidden rounded-2xl bg-[#1f0a3f] border border-white/10 hover:border-pink-400/60 transition shadow-xl hover:shadow-pink-900/30 h-full"
      data-testid={testid || `news-card-${a.id}`}
    >
      <div className={`relative w-full overflow-hidden ${isXl ? "aspect-[16/10]" : "aspect-[16/10]"}`}>
        {a.image ? (
          <img
            src={a.image}
            alt={a.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
            <Newspaper size={48} className="text-pink-300/40"/>
          </div>
        )}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 40%, rgba(22,7,50,0.92) 100%)" }}/>
        {!!(a.categories || []).length && (
          <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
            {a.categories.slice(0, 2).map((c) => (
              <span key={c.slug} className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-white shadow-lg" style={{ background: SECONDARY }}>
                {c.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
        <div className="flex items-center gap-2 text-pink-300 text-[11px] font-black uppercase tracking-widest mb-2">
          <CalendarIcon size={11}/>
          <span>{fmtDate(a.date_iso)}</span>
        </div>
        <h3
          className={`text-white font-blackhawk italic uppercase leading-tight ${isXl ? "text-2xl sm:text-4xl" : "text-base sm:text-lg"}`}
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}
        >
          {a.title}
        </h3>
        {isXl && a.excerpt && (
          <p className="mt-2 text-white/70 text-sm line-clamp-2">{a.excerpt}</p>
        )}
        <div className="mt-3 inline-flex items-center gap-2 text-pink-300 group-hover:text-pink-200 text-xs font-black uppercase tracking-widest transition">
          Lire l'article <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform"/>
        </div>
      </div>
    </Link>
  );
}

export default function SportActualites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");
  const [activeTag, setActiveTag] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    api.get("/sport/news?limit=80")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  // Honor ?cat= URL param
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("cat");
    if (!cat) return;
    if (CATEGORY_FILTERS.find((c) => c.slug === cat)) setActiveCat(cat);
    else if (EDITORIAL_TAGS.find((t) => t.slug === cat)) setActiveTag(cat);
  }, [loading]);

  // Reset pagination when filters change
  useEffect(() => { setVisible(PAGE_SIZE); }, [activeCat, activeTag]);

  // Hero magazine: latest article (always from full list before filters)
  const hero = items[0];
  const sub = items.slice(1, 5);

  // Filtered list (excludes the 5 hero items to avoid duplicates)
  const filtered = useMemo(() => {
    const heroIds = new Set([hero, ...sub].filter(Boolean).map((x) => x.id));
    return items.filter((a) => {
      if (heroIds.has(a.id)) return false;
      if (activeCat !== "all") {
        const slugs = (a.categories || []).map((c) => c.slug);
        if (!slugs.includes(activeCat)) return false;
      }
      if (activeTag !== "all") {
        if (!(a.tags || []).includes(activeTag)) return false;
      }
      return true;
    });
  }, [items, hero, sub, activeCat, activeTag]);

  const shown = filtered.slice(0, visible);
  const hasMore = filtered.length > visible;

  return (
    <div style={{ background: BG_DARK }} data-testid="sport-actualites-page">
      {/* HERO MAGAZINE */}
      <section className="relative pt-32 pb-10 sm:pt-40 sm:pb-14 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-60" style={{
          background: `radial-gradient(ellipse at 80% 0%, rgba(236,31,124,0.22), transparent 55%), radial-gradient(ellipse at 0% 100%, rgba(79,27,124,0.55), transparent 60%)`,
        }}/>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-1.5 h-6 sm:h-8 rounded-full bg-pink-400"/>
            <span className="text-pink-300 font-black uppercase tracking-[0.3em] text-[11px] sm:text-xs">L'actualité du club · #IPHB</span>
          </div>
          <h1 className="text-white font-blackhawk italic uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mb-4">
            Toutes les <span style={{ color: SECONDARY }}>actus</span><br/>
            d'Istres Provence Handball
          </h1>
          <p className="text-white/70 max-w-2xl text-sm sm:text-base">
            Compte-rendus, avant-matchs, interviews, coulisses, infos partenaires et vie du club —
            la rédaction officielle vous tient informé en temps réel.
          </p>

          {loading ? (
            <div className="mt-12 grid lg:grid-cols-3 gap-5">
              {[1,2,3].map((i) => <div key={i} className="aspect-[16/10] rounded-2xl bg-white/5 animate-pulse"/>)}
            </div>
          ) : !items.length ? (
            <div className="mt-12 text-center py-20 rounded-2xl bg-white/5 border border-white/10">
              <Newspaper size={40} className="mx-auto text-pink-300/60 mb-4"/>
              <p className="text-white/70">Aucune actualité disponible pour le moment.</p>
            </div>
          ) : (
            <div className="mt-10 grid lg:grid-cols-3 gap-5" data-testid="news-hero-grid">
              {hero && (
                <div className="lg:col-span-2 lg:row-span-2">
                  <ArticleCard a={hero} size="xl" testid="news-hero-main"/>
                </div>
              )}
              <div className="grid grid-cols-2 lg:grid-cols-1 gap-5">
                {sub.slice(0, 2).map((a) => (
                  <ArticleCard key={a.id} a={a} size="md" testid={`news-hero-sub-${a.id}`}/>
                ))}
              </div>
              {sub.slice(2, 4).map((a) => (
                <ArticleCard key={a.id} a={a} size="md" testid={`news-hero-sub-${a.id}`}/>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FILTER BANDS */}
      {items.length > 0 && (
        <section className="relative py-6 border-y border-white/5" style={{ background: BG_DEEP }} data-testid="news-filter-band">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 space-y-3">
            {/* Category row */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-2 text-pink-300 font-black uppercase tracking-widest text-[11px] mr-2 shrink-0">
                <Tag size={13}/> Catégorie
              </span>
              {CATEGORY_FILTERS.map((c) => {
                const count = c.slug === "all"
                  ? items.length
                  : items.filter((it) => (it.categories || []).map((x) => x.slug).includes(c.slug)).length;
                if (c.slug !== "all" && count === 0) return null;
                const active = activeCat === c.slug;
                return (
                  <button
                    key={c.slug}
                    onClick={() => setActiveCat(c.slug)}
                    className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest transition ${active ? "text-white shadow-lg" : "text-white/70 hover:text-white bg-white/5 border border-white/10"}`}
                    style={active ? { background: SECONDARY } : {}}
                    data-testid={`news-filter-cat-${c.slug}`}
                  >
                    {c.label} ({count})
                  </button>
                );
              })}
            </div>
            {/* Editorial tag row */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-2 text-pink-300/80 font-black uppercase tracking-widest text-[11px] mr-2 shrink-0">
                <Newspaper size={13}/> Type
              </span>
              <button
                onClick={() => setActiveTag("all")}
                className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition ${activeTag === "all" ? "text-white shadow-lg" : "text-white/70 hover:text-white bg-white/5 border border-white/10"}`}
                style={activeTag === "all" ? { background: PRIMARY } : {}}
                data-testid="news-filter-tag-all"
              >
                Tous types
              </button>
              {EDITORIAL_TAGS.map((t) => {
                const count = items.filter((i) => (i.tags || []).includes(t.slug)).length;
                if (!count) return null;
                const active = activeTag === t.slug;
                return (
                  <button
                    key={t.slug}
                    onClick={() => setActiveTag(t.slug)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition ${active ? "text-white shadow-lg" : "text-white/70 hover:text-white bg-white/5 border border-white/10"}`}
                    style={active ? { background: PRIMARY } : {}}
                    data-testid={`news-filter-tag-${t.slug}`}
                  >
                    {t.label} ({count})
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* GRID + LOAD MORE */}
      {items.length > 0 && (
        <section className="relative py-12 sm:py-16" data-testid="news-grid-section">
          <div className="max-w-7xl mx-auto px-4 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <div className="text-[11px] font-black tracking-[0.3em] uppercase text-pink-300">À la une</div>
                <h2 className="text-white font-blackhawk italic uppercase text-2xl sm:text-4xl leading-tight mt-1">
                  {activeCat === "all" && activeTag === "all"
                    ? "Toutes les actualités"
                    : (CATEGORY_FILTERS.find((c) => c.slug === activeCat)?.label || "Tous") +
                      (activeTag !== "all" ? " · " + EDITORIAL_TAGS.find((t) => t.slug === activeTag)?.label : "")}
                </h2>
              </div>
              <span className="text-white/40 text-xs font-bold tabular-nums shrink-0 ml-4">{filtered.length} articles</span>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 rounded-2xl bg-white/5 border border-white/10">
                <Newspaper size={36} className="mx-auto text-pink-300/60 mb-4"/>
                <p className="text-white/70">Aucun article ne correspond à ces filtres.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="news-grid">
                  {shown.map((a) => (
                    <div key={a.id} className="h-[340px]">
                      <ArticleCard a={a}/>
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="mt-10 flex justify-center">
                    <button
                      onClick={() => setVisible((v) => v + PAGE_SIZE)}
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-2xl hover:scale-105 transition"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
                      data-testid="news-load-more"
                    >
                      Charger plus d'articles ({filtered.length - visible}) <ChevronDown size={16}/>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* CTA bottom */}
      <section className="relative py-14 sm:py-20 text-center" style={{ background: BG_DEEP }}>
        <div className="max-w-3xl mx-auto px-4">
          <h3 className="text-white font-blackhawk italic uppercase text-3xl sm:text-5xl leading-tight">
            Ne ratez aucune <span style={{ color: SECONDARY }}>news</span>
          </h3>
          <p className="text-white/70 mt-4 text-sm sm:text-base">
            Inscrivez-vous à la newsletter et recevez les avant-matchs, comptes-rendus et coulisses directement par e-mail.
          </p>
          <a
            href="#footer-newsletter"
            onClick={(e) => { e.preventDefault(); document.querySelector("[data-testid='footer-newsletter']")?.scrollIntoView({ behavior: "smooth" }); }}
            className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-2xl hover:scale-105 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
            data-testid="news-cta-newsletter"
          >
            S'abonner à la newsletter <ArrowRight size={16}/>
          </a>
        </div>
      </section>
    </div>
  );
}
